// Klasyfikacja warstwy na grupy litologiczne (tryb autoConnectMode = 'bySoilGroup').
//
// ZASADA: grupa idzie za frakcja GLOWNA (rzeczownikiem), domieszka (przymiotnik)
// nie ma glosu. Dzieki temu "Gp" (glina piaszczysta) trafia do glin, a "Pg"
// (piasek gliniasty) do piaskow — te same dwie frakcje, odwrotna kolejnosc,
// rozne grupy. Tak samo "πp" (pyl piaszczysty) → pyly, "Nmp" (namul piaszczysty)
// → organiczne.
//
// Kolejnosc dopasowania: od NAJDLUZSZEGO kodu. Bez tego "G" polknie "Gπz", "Gp"
// i cala reszte glin — ta sama pulapka co "//" przed "/" przy separatorach.

export const SOIL_GROUPS = {
  NASYP: 'nasyp',
  ORGANICZNE: 'organiczne',
  PYLY: 'pyly',
  GLINY: 'gliny',
  ILY: 'ily',
  ZWIRY: 'zwiry',
  POSPOLKI: 'pospolki',
  PIASKI: 'piaski',
  ZWIETRZELINA: 'zwietrzelina',
  SKALA: 'skala',
};

// Etykiety do UI / legendy.
export const SOIL_GROUP_LABELS = {
  [SOIL_GROUPS.NASYP]: 'Nasypy / gleba',
  [SOIL_GROUPS.ORGANICZNE]: 'Organiczne',
  [SOIL_GROUPS.PYLY]: 'Pyły',
  [SOIL_GROUPS.GLINY]: 'Gliny',
  [SOIL_GROUPS.ILY]: 'Iły',
  [SOIL_GROUPS.ZWIRY]: 'Żwiry',
  [SOIL_GROUPS.POSPOLKI]: 'Pospółki',
  [SOIL_GROUPS.PIASKI]: 'Piaski',
  [SOIL_GROUPS.ZWIETRZELINA]: 'Zwietrzeliny',
  [SOIL_GROUPS.SKALA]: 'Skały',
};

// Kolory grup — zeby przekroj byl spojny mimo dziur w palecie per-litologia
// (`layerColors.js` nie zna m.in. π, πp, G, Gp, Ż — leciałyby na domyślny szary).
export const SOIL_GROUP_COLORS = {
  [SOIL_GROUPS.NASYP]: '#A98E5C',
  [SOIL_GROUPS.ORGANICZNE]: '#4A5D3A',
  [SOIL_GROUPS.PYLY]: '#C9B896',
  [SOIL_GROUPS.GLINY]: '#A0522D',
  [SOIL_GROUPS.ILY]: '#8B6F5C',
  [SOIL_GROUPS.ZWIRY]: '#D9A441',
  [SOIL_GROUPS.POSPOLKI]: '#E0B870',
  [SOIL_GROUPS.PIASKI]: '#F0C870',
  [SOIL_GROUPS.ZWIETRZELINA]: '#DAA520',
  [SOIL_GROUPS.SKALA]: '#808080',
};

// Wyjatki sprawdzane PRZED regula ogolna — kody ktore lamalyby dopasowanie
// po pierwszej literze. "Po" (pospolka) zaczyna sie od P, wiec bez tego wpadloby
// do piaskow.
const EXCEPTIONS = [
  { code: 'Po', group: SOIL_GROUPS.POSPOLKI },
  // Kody jednoliterowe trzymamy jako dopasowanie DOKLADNE — jako prefiks "T"/"D"
  // lapaloby przypadkiem kazdy przyszly skrot zaczynajacy sie ta litera.
  { code: 'T', group: SOIL_GROUPS.ORGANICZNE },
  { code: 'D', group: SOIL_GROUPS.ORGANICZNE },
];

// Prefiksy per grupa, dopasowywane case-sensitive (skroty PN rozrozniaja
// wielkosc liter: "Ps" piasek sredni vs "PS"...). Sortowane po dlugosci malejaco
// przy budowie tablicy ponizej.
const PREFIXES = [
  // Nasypy / gleba
  { p: 'Nasyp', group: SOIL_GROUPS.NASYP },
  { p: 'nN', group: SOIL_GROUPS.NASYP },
  { p: 'Gb', group: SOIL_GROUPS.NASYP },
  // Organiczne — "Nm" (namuly) lapie Nmg/Nmp. Torf "T" i drewno "D" siedza
  // w EXCEPTIONS jako dopasowanie dokladne.
  { p: 'Nm', group: SOIL_GROUPS.ORGANICZNE },
  // Skaly i zwietrzeliny — MUSZA byc przed gruntami, bo "ST"/"SM" nie koliduja,
  // ale KR/KW tak samo jak w getWeatheringProfile.
  { p: 'SM', group: SOIL_GROUPS.SKALA },
  { p: 'ST', group: SOIL_GROUPS.SKALA },
  { p: 'Brekcja', group: SOIL_GROUPS.SKALA },
  { p: 'Brekacja', group: SOIL_GROUPS.SKALA }, // literowka wystepujaca w kodzie/danych
  { p: 'KRg', group: SOIL_GROUPS.ZWIETRZELINA },
  { p: 'KR', group: SOIL_GROUPS.ZWIETRZELINA },
  { p: 'KWg', group: SOIL_GROUPS.ZWIETRZELINA },
  { p: 'KW', group: SOIL_GROUPS.ZWIETRZELINA },
  // Gliny
  { p: 'Gπz', group: SOIL_GROUPS.GLINY },
  { p: 'Gπ', group: SOIL_GROUPS.GLINY },
  { p: 'Gz', group: SOIL_GROUPS.GLINY },
  { p: 'Gp', group: SOIL_GROUPS.GLINY },
  { p: 'G', group: SOIL_GROUPS.GLINY },
  // Ily
  { p: 'Iπ', group: SOIL_GROUPS.ILY },
  { p: 'I', group: SOIL_GROUPS.ILY },
  // Pyly (symbol π oraz zapis "Pi" spotykany w danych bez polskich znakow)
  { p: 'πp', group: SOIL_GROUPS.PYLY },
  { p: 'π', group: SOIL_GROUPS.PYLY },
  // Zwiry
  { p: 'Żg', group: SOIL_GROUPS.ZWIRY },
  { p: 'Ż', group: SOIL_GROUPS.ZWIRY },
  // Piaski — wszystko co JEST piaskiem, z dowolna domieszka
  { p: 'P', group: SOIL_GROUPS.PIASKI },
];

// Najdluzsze prefiksy pierwsze.
const SORTED_PREFIXES = [...PREFIXES].sort((a, b) => b.p.length - a.p.length);

/**
 * Zwraca grupe litologiczna dla nazwy warstwy.
 * Nazwa moze byc compositem ("Ps // Pd", "Gπ / π") — liczy sie PIERWSZY slot,
 * tak samo jak przy kolorze warstwy (resolveLayerColor bierze lithologyRockSoil01).
 *
 * @param {string} layerName - nazwa warstwy / skrot litologii
 * @returns {string} klucz z SOIL_GROUPS, lub '' gdy nie rozpoznano
 */
export function getSoilGroup(layerName) {
  if (!layerName || layerName.trim() === '') return '';
  // Pierwszy slot compositu: obcinamy po separatorze przewarstwienia i po nawiasie
  // podkategorii ("SM(Łi)" → "SM", "Pd(g)" → "Pd").
  const first = layerName.trim().split(/\s*(?:\/\/|\/|\+)\s*/)[0].trim();
  const base = first.split('(')[0].trim();
  if (!base) return '';

  const exact = EXCEPTIONS.find(e => e.code === base);
  if (exact) return exact.group;

  const hit = SORTED_PREFIXES.find(e => base.startsWith(e.p));
  return hit ? hit.group : '';
}
